import type { ReactNode } from 'react'
import React, { useState } from 'react'
import { Button, InputGroup } from '@systemeio/components'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { BadRequest } from 'shared/errors/bad-request'
import { registerCommunity } from '../api/community-api'

function RegisterForm({
  showThankYou,
  disabled,
}: {
  showThankYou: () => void
  disabled: boolean
  isJoinFlow?: boolean
}): ReactNode {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')

  const router = useRouter()
  const { communityPath, invitationToken, confirmationToken } = router.query

  const registration = useMutation({
    mutationFn: (email: string) => {
      const params = new URLSearchParams()
      if (invitationToken) {
        params.set('invitationToken', invitationToken)
      }
      if (confirmationToken) {
        params.set('confirmationToken', confirmationToken)
      }
      const paramsStr = params.toString()
      return registerCommunity(
        email,
        `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${globalThis.location.host}/community/${communityPath}${paramsStr ? `?${paramsStr}` : ''}`,
      )
    },
    onSuccess: showThankYou,
    retry: false,
  })
  const errors = registration.error instanceof BadRequest ? registration.error.errors : undefined
  const emailError = errors?.fields?.email?.join('\n') ?? ''
  const commonError = errors?.common?.join('\n') ?? ''

  function changeEmail(e: React.ChangeEvent<HTMLInputElement>): void {
    setEmail(e.target.value)
  }

  return (
    <>
      <div key="email" className="flex flex-auto flex-col gap-1">
        <label className="text-sm font-medium text-text-primary">{t('global.email_address')}</label>
        <InputGroup.Root>
          <InputGroup.Input
            data-testid="community-register-email"
            type="email"
            value={email}
            onChange={changeEmail}
            maxLength={180}
            disabled={disabled}
          />
        </InputGroup.Root>
        {emailError && <p className="text-sm text-text-error">{emailError}</p>}
      </div>
      <Button
        key="register"
        data-testid="community-register-submit"
        variant="primary"
        isLoading={registration.isPending}
        onClick={() => registration.mutate(email)}
        disabled={disabled}
      >
        {t('join_form.button.register.title')}
      </Button>
      {commonError && (
        <p data-testid="community-register-common-error" className="text-sm text-text-error">
          {commonError}
        </p>
      )}
    </>
  )
}

export default RegisterForm
